import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { filterBy } from '@progress/kendo-data-query';
import {
    ComboBox as KendoComboBox,
    ComboBoxChangeEvent,
    ComboBoxCloseEvent,
    ComboBoxFilterChangeEvent,
    ComboBoxHandle,
    ComboBoxOpenEvent,
    ComboBoxProps,
    ListItemProps,
} from '@progress/kendo-react-dropdowns';
import { InputSuffix } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import cn from 'clsx';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import InformationIcon from '@components-clean/core/icons/information-icon';
import { TextBox } from '@components-clean/core/text-box';
import Tooltip from '@components-clean/core/tooltip';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import ValidationIconSuffix from 'views-clean/components/core/icons/validation-icon-suffix';
import { useBoolean } from '@hooks/index';

export type ExcludeArray<T> = T extends Array<unknown> ? never : T;

export interface DropDownListProps<T> extends ComboBoxProps {
    'data-testid': string;
    data: ExcludeArray<T>[];
    value?: ExcludeArray<T>;
    inline?: boolean;
    hintText?: string;
    suffix?: ReactElement;
    hideBorder?: boolean;
    readOnly?: boolean;
    ignoreReadonlyContext?: boolean;
    activeField?: string;
    tooltipText?: string;
    tooltipClassName?: string;
    type?: 'default' | 'card-header';
    showInactiveIconInList?: boolean;
    showPlaceholder?: boolean;
}

const DropDownList = <T,>({
    onOpen,
    onClose,
    className,
    tooltipClassName,
    data,
    valid,
    validationMessage,
    placeholder,
    label,
    inline,
    filterable = true,
    hintText,
    'data-testid': testId,
    disabled,
    suffix,
    hideBorder = false,
    readOnly,
    ignoreReadonlyContext,
    activeField,
    tooltipText,
    type = 'default',
    showInactiveIconInList = false,
    showPlaceholder = true,
    ...props
}: DropDownListProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const isDirty = useBoolean(false);
    const { t } = useTranslation();
    const [datasource, setDatasource] = useState<T[]>([]);
    const ref = useRef<ComboBoxHandle>();

    const { readOnly: readonlyCtx } = useReadOnlyContext();

    useEffect(() => {
        ref.current?.element?.setAttribute('data-testid', testId);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setDatasource(data ? [...data] : []);
    }, [data]);

    const onFilterChange = (event: ComboBoxFilterChangeEvent) => {
        setDatasource(filterBy([...data], event.filter));
    };

    // Add an open handler so that we can add a class to determine if the combo box is open
    // This is needed so that we can override the style when it's open
    const onOpenHandler = (event: ComboBoxOpenEvent) => {
        setIsOpen(true);

        if (onOpen) {
            onOpen(event);
        }
    };

    const clearFilter = () => {
        const newData = filterBy([...data], {
            value: '',
            operator: 'contains',
            ignoreCase: true,
            field: props.textField,
        });
        isDirty.setFalse();
        setDatasource(newData);
    };

    // Add a close handler so that we can remove a class to determine if the combo box is open
    // This is needed so that we can override the style when it's open
    const onCloseHandler = (event: ComboBoxCloseEvent) => {
        setIsOpen(false);

        if (event.nativeEvent?.type === 'keydown' || isDirty.value) {
            clearFilter();
        }

        if (onClose) {
            onClose(event);
        }
    };

    const getActiveValue = () => {
        const { value } = props;
        if (value && typeof value === 'object' && activeField && Object.hasOwn(value, activeField)) {
            // @ts-ignore
            return Boolean(value[activeField] as unknown);
        }
        return true;
    };

    const renderComboBoxSuffix = (): ReactElement => {
        return (
            <div className="k-display-flex k-align-items-center">
                {valid === false && <ValidationIconSuffix validationMessage={validationMessage} />}
                {!getActiveValue() && (
                    <InputSuffix>
                        <InactiveIcon dataTestId={`${testId}-inactive`} isSuffix tooltipPosition="right" />
                    </InputSuffix>
                )}
                {!!suffix && <InputSuffix>{suffix}</InputSuffix>}
            </div>
        );
    };

    const itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
        let isActive = true;
        if (itemProps.dataItem && activeField && Object.hasOwn(itemProps.dataItem as ListItemProps, activeField)) {
            isActive = Boolean(itemProps.dataItem[activeField]);
        }
        const itemChildren = (
            <div className="k-display-flex k-w-full k-align-items-center k-justify-content-between">
                {li.props.children as Iterable<ReactNode>}
                {!isActive && showInactiveIconInList && (
                    <InactiveIcon dataTestId={`${testId}-inactive`} tooltipPosition="right" />
                )}
            </div>
        );

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/no-clone-element
        return React.cloneElement(li, li.props, itemChildren);
    };

    if (readOnly || (readonlyCtx && !ignoreReadonlyContext)) {
        let textValue: string = '';
        let active = true;
        const { textField } = props;
        try {
            const val = props.value;
            if (val != null) {
                if (typeof val === 'object') {
                    if (textField && Object.hasOwn(val, textField)) {
                        // @ts-ignore
                        textValue = String(val[textField] as unknown);
                    }
                    active = getActiveValue();
                } else {
                    textValue = val.toString();
                }
            }
        } catch {
            window.console.error('Could not determine text value for readonly drop down list');
        }
        return (
            <TextBox
                data-testid={testId}
                name={props.name}
                value={textValue}
                readOnly
                label={label}
                fillMode={props.fillMode}
                inactiveValue={!active}
                type={type}
                inline={inline}
                suffix={suffix}
            />
        );
    }

    const classes = cn(className, {
        'opra-combobox-open': isOpen,
        '!k-border-hidden': hideBorder,
        'opra-inactive-value': !getActiveValue(),
        'card-header': type === 'card-header',
    });

    const kendoTextBox = (
        <KendoComboBox
            ref={ref}
            {...props}
            data={datasource}
            filterable={filterable}
            onFilterChange={props.onFilterChange || onFilterChange}
            onOpen={onOpenHandler}
            onClose={onCloseHandler}
            placeholder={!disabled && showPlaceholder ? (placeholder ?? t('cm2SelectValue')) : ''}
            value={disabled ? null : props.value}
            disabled={disabled}
            className={classes}
            valid={valid}
            suffix={renderComboBoxSuffix}
            itemRender={itemRender}
            onChange={(event) => {
                // Use isDirty to indicate the onCloseHandler might need to do the clearing of filter value
                isDirty.setTrue();
                if (event.nativeEvent?.type === 'click') {
                    clearFilter();
                } else if (event.nativeEvent?.type === 'keydown') {
                    if (!['ArrowDown', 'ArrowUp'].some((k) => (event.nativeEvent as KeyboardEvent).code === k)) {
                        clearFilter();
                    }
                }

                props.onChange && props.onChange(event);
            }}
        />
    );

    let tooltipKendoComboBox;

    if (disabled && tooltipText) {
        tooltipKendoComboBox = (
            <Tooltip position="bottom" isInactive={false} tooltipClassName={tooltipClassName}>
                <span title={tooltipText}> {kendoTextBox} </span>
            </Tooltip>
        );
    } else {
        tooltipKendoComboBox = kendoTextBox;
    }

    return (
        <div
            className={cn('drop-down-list k-w-full', {
                'k-flex-grow': !props.style?.width,
                'k-d-flex k-gap-4 k-align-items-center': inline,
            })}
        >
            {label && label.length > 0 && (
                <Label
                    editorRef={ref}
                    className={cn('k-d-flex k-align-items-end', {
                        '!k-pb-0 k-white-space-nowrap': inline,
                    })}
                    style={{ lineHeight: inline ? '1rem' : undefined }}
                >
                    {label}
                    {hintText && (
                        <InformationIcon title={hintText} className="k-ml-1.5" dataTestId={`${testId}-hint`} />
                    )}
                </Label>
            )}
            {tooltipKendoComboBox}
        </div>
    );
};

interface DropDownListControllerProps<TData, TControl extends FieldValues> extends DropDownListProps<TData> {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean>;
    handleInputChange?: (event: ComboBoxChangeEvent) => void;
}

const DropDownListController = <TData, TControl extends FieldValues>({
    control,
    name,
    data,
    disabled = false,
    'data-testid': dataTestId,
    label,
    textField,
    dataItemKey,
    required = false,
    validateRules,
    groupField,
    fillMode = 'solid',
    handleInputChange,
    loading,
    ...props
}: DropDownListControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    ...validateRules,
                },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <DropDownList
                    {...props}
                    value={value as ExcludeArray<TData>}
                    onChange={(event) => {
                        onChange(event);
                        if (handleInputChange) handleInputChange(event);
                    }}
                    data={data}
                    data-testid={dataTestId}
                    label={label}
                    disabled={disabled}
                    textField={textField}
                    dataItemKey={dataItemKey}
                    valid={!error}
                    validationMessage={error?.message}
                    groupField={groupField}
                    fillMode={fillMode}
                    loading={loading}
                />
            )}
        />
    );
};

export { DropDownList, DropDownListController };
